// @flow

import _ from 'lodash';
import { batchActions } from 'redux-batched-actions';

import {
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
} from '@zapier/toolbox-redux-framework';

import { arrayToObjectByKey } from 'app/common/CommonUtils';
import {
  DELIVERED_STATUS,
  READ_STATUS,
  makeNotifications,
} from 'app/viewport/utils/notification';
import { getKeyInStorage, setKeyInStorage } from '@zapier/local-storage';

export const type = 'userNotification';

// No actual backend for this, stored locally.
const API_URL = 'seenNotifications';

type NotificationType = {
  id: number | string,
};

const loadNotifications = async () => {
  return Promise.resolve(getKeyInStorage(API_URL) || {});
};

const saveNotifications = async (notifications: NotificationType[]) => {
  const newNotifications = arrayToObjectByKey('id', notifications);
  const existingNotifications = await loadNotifications();
  const allNotifications = { ...existingNotifications, ...newNotifications };
  return setKeyInStorage(API_URL, allNotifications);
};

export const api = buildEntityApi(type, API_URL, userNotificationApi => {
  userNotificationApi.loadCollection = async getState => {
    const savedNotifications = await loadNotifications();
    const notifications = makeNotifications(savedNotifications, getState());
    return { objects: notifications };
  };
});

export const selectors = buildEntitySelectors(type, sel => {
  sel.createdAt = _.partial(sel.dataItem, 'createdAt');
  sel.isDismissed = _.partial(sel.dataItem, 'isDismissed');
  sel.all.notDismissed = _.partial(sel.all.filterBy, _.negate(sel.isDismissed));
});

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  entityActions => {
    const origCreateCollectionUiEntity = entityActions.createCollectionUiEntity;
    entityActions.createCollectionUiEntity = (data = {}, state) => {
      return origCreateCollectionUiEntity(
        {
          ...data,
          createdAt: Date.now(),
        },
        state
      );
    };

    entityActions.updateNotificationStatus = _.partial(
      entityActions.updateEntityDataItem,
      'notificationStatus'
    );

    entityActions.markNotifications = (status, notifications) => (
      dispatch,
      getState
    ) => {
      const batchedActions = notifications.map(n =>
        entityActions.updateNotificationStatus(status, n.id)
      );
      dispatch(batchActions(batchedActions));
      return saveNotifications(selectors.all.entitiesData(getState()));
    };

    entityActions.markNotificationsDelivered = _.partial(
      entityActions.markNotifications,
      DELIVERED_STATUS
    );

    entityActions.markNotificationsRead = _.partial(
      entityActions.markNotifications,
      READ_STATUS
    );

    entityActions.dismiss = _.partial(
      entityActions.updateEntityDataItem,
      'isDismissed',
      true
    );

    entityActions.buildForEntity = (
      notification: Object,
      cid: number | string
    ) => {
      return entityActions.createCollectionUiEntity({
        buttonText: undefined,
        hasBadge: false,
        hasButton: false,
        id: `user-notification-for-${cid}`,
        isImportant: true,
        message: undefined,
        notificationStatus: 'new',
        title: undefined,
        type,
        ...notification,
      });
    };
  }
);
